/*
 * <featured-showcase> — crossfading slideshow with a thumbnail tablist.
 * The thumbnails are an ARIA tablist: Left/Right (and Home/End) switch slides
 * per the tabs keyboard pattern, and each slide is a tab panel that crossfades
 * in. Inactive slides are `inert` so only the active slide's content reaches
 * the keyboard / assistive tech. Optional autoplay, paused while a keyboard user
 * is focused inside and disabled under reduced motion (no hover pause); with it
 * comes a pause / play button, since slides that change by themselves must be
 * stoppable by everyone (WCAG 2.2.2).
 * Guarded so a repeated section only defines the element once.
 */
if (!customElements.get('featured-showcase')) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FeaturedShowcase extends HTMLElement {
    connectedCallback() {
      this.slidesEl = this.querySelector('[data-showcase-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-showcase-slide]'));
      this.panels = this.slides.map((slide) => slide.closest('.shopify-block') || slide);
      this.thumbs = Array.from(this.querySelectorAll('[data-showcase-thumb]'));
      this.toggleBtn = this.querySelector('[data-showcase-toggle]');
      if (!this.slidesEl || this.slides.length === 0) return;

      this.index = 0;
      this.count = this.slides.length;
      this.autoplay = this.dataset.autoplay === 'true' && this.count > 1;
      this.speed = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;
      this.focused = false;
      this.paused = false; // by the visitor, with the pause button

      // Wire ARIA: tab[i] <-> panel[i], and label each slide "N of M".
      const uid = Math.random().toString(36).slice(2, 8);
      this.slides.forEach((slide, i) => {
        const panelId = `hs-panel-${uid}-${i}`;
        slide.id = panelId;
        slide.setAttribute('aria-label', `${i + 1} of ${this.count}`);
        const thumb = this.thumbs[i];
        if (thumb) {
          const tabId = `hs-tab-${uid}-${i}`;
          thumb.id = tabId;
          thumb.setAttribute('aria-controls', panelId);
          slide.setAttribute('aria-labelledby', tabId);
          // The section can't read the slides' settings, so it can only label
          // tabs "Slide N". Name each after its slide instead: the heading,
          // else the eyebrow. Screen readers add "tab N of M" themselves.
          const heading = slide.querySelector('.showcase__text :is(h1, h2, h3, h4, h5, h6)');
          const eyebrow = slide.querySelector('.showcase__eyebrow');
          const name = ((heading && heading.textContent) || (eyebrow && eyebrow.textContent) || '').trim();
          if (name) thumb.setAttribute('aria-label', name);
        }
      });

      // Once per element: the theme editor moves nodes, which runs this again
      // on the same thumbnails - and a second set of listeners would step two
      // slides for every arrow key.
      if (!this.wired) {
        this.wired = true;
        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => this.select(i, true));
          thumb.addEventListener('keydown', (event) => this.onKey(event));
        });

        this.addEventListener('focusin', () => {
          this.focused = true;
          this.updateAutoplay();
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.focused = false;
            this.updateAutoplay();
          }
        });

        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.paused = !this.paused;
            this.updateAutoplay();
          });
        }
      }

      this.onVisibility = () => this.updateAutoplay();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onReduceChange = () => this.updateAutoplay();
      reduce.addEventListener('change', this.onReduceChange);

      this.slidesEl.setAttribute('data-ready', '');
      this.setActive(0);
      this.updateAutoplay();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduce.removeEventListener('change', this.onReduceChange);
      clearTimeout(this.timer);
    }

    setActive(i) {
      this.index = i;
      this.panels.forEach((panel, pi) => panel.classList.toggle('is-active', pi === i));
      this.slides.forEach((slide, si) => {
        if (si === i) {
          slide.removeAttribute('inert');
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      this.thumbs.forEach((thumb, ti) => {
        const selected = ti === i;
        thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
        thumb.tabIndex = selected ? 0 : -1;
      });
    }

    // User-driven change: wraps, moves focus to the thumb, resets the timer.
    select(i, focusThumb) {
      const n = this.count;
      const idx = ((i % n) + n) % n;
      this.setActive(idx);
      if (focusThumb && this.thumbs[idx]) this.thumbs[idx].focus();
      this.updateAutoplay();
    }

    onKey(event) {
      let target = null;
      switch (event.key) {
        case 'ArrowLeft':
          target = this.index - 1;
          break;
        case 'ArrowRight':
          target = this.index + 1;
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = this.count - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(target, true);
    }

    isPlaying() {
      return this.autoplay && !this.paused && !reduce.matches && !this.focused && !document.hidden;
    }

    updateAutoplay() {
      clearTimeout(this.timer);
      if (this.toggleBtn) {
        // There is something to pause only where rotation can run at all.
        this.toggleBtn.hidden = !this.autoplay || reduce.matches;
        this.toggleBtn.setAttribute('aria-pressed', String(this.paused));
      }
      if (!this.isPlaying()) return;
      this.timer = setTimeout(() => {
        this.setActive((this.index + 1) % this.count);
        this.updateAutoplay();
      }, this.speed);
    }
  }

  customElements.define('featured-showcase', FeaturedShowcase);
}
